import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemChip, ActionChip } from '../pills';
import { OptToken } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { ConfirmDialog } from '../ConfirmDialog';
import { useListFetch } from '../useListFetch';
import { useWriteFeedback } from '../useWriteFeedback';

/**
 * RentRollCard — Part 1 of the real-estate module (realestate-dashboard-prd.md §1). The FIRST section
 * extracted from the PortfolioTab monolith into its own <SectionCard/> file — the pattern Parts 2–5
 * each follow (their own component file) so parallel engineers don't collide in one file. PortfolioTab
 * COMPOSES this; the other sections' mock blocks stay inline for now (per-section conversion, §0.2).
 *
 * Real rent-roll rows backed by Lease + current-period Invoice (GET /api/properties/rent-roll). The
 * three WhatsApp reachability states are computed server-side AT FETCH (never on tap) and rendered
 * before any interaction:
 *   - sendable      → a genuinely tappable "Send WhatsApp reminder" ActionChip (confirm-first).
 *   - window-closed → a NON-interactive note, no tap target (an action you can't take must not look
 *                     tappable; and it is NOT a disabled button — disabled reads "resolves on its own",
 *                     which is false: the window only reopens when the tenant re-initiates).
 *   - not-connected → no per-row chrome; the single page-level banner (owned by PortfolioTab) discloses
 *                     the one account-level cause (§1.3 point 3 / AC1.3a).
 */

type RentStatus = 'vacant' | 'unbilled' | 'paid' | 'late' | 'due-soon' | 'open';
type Reachability = 'sendable' | 'window-closed' | 'not-connected';

interface RentRollRow {
  unitId: string;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  tenantLeadId: string | null;
  tenantName: string | null;
  rent: number | null;
  status: RentStatus;
  invoiceId: string | null;
  invoiceStatus: string | null;
  dueDate: string | null;
  reachability: Reachability | null;
  remindedAt: string | null;
}
interface RentRoll {
  multiProperty: boolean;
  whatsappConnected: boolean;
  anyUnbilled: boolean;
  rows: RentRollRow[];
}

// Status → chip token (whole strings only — optClasses()/SystemChip resolve the literal Tailwind
// classes; never a `bg-opt-${x}` fragment the JIT would purge). 'unbilled' is muted (no token).
const STATUS_TOKEN: Partial<Record<RentStatus, OptToken>> = {
  paid: 'emerald',
  late: 'rose',
  'due-soon': 'amber',
  open: 'slate',
  vacant: 'amber', // ruled (cmdr): no listing model exists, so a vacant unit is just "Vacant", not "Listing live"
};

function statusLabel(row: RentRollRow, t: (k: string, o?: any) => string): string {
  switch (row.status) {
    case 'paid': return t('portfolio.rentRoll.status.paid');
    case 'vacant': return t('portfolio.rentRoll.status.vacant');
    case 'unbilled': return t('portfolio.rentRoll.status.unbilled');
    case 'open': return t('portfolio.rentRoll.status.open');
    case 'late': {
      const days = row.dueDate ? Math.floor((Date.now() - new Date(row.dueDate).getTime()) / 86400000) : 0;
      return t('portfolio.rentRoll.status.late', { count: Math.max(days, 1) });
    }
    case 'due-soon': {
      const days = row.dueDate ? Math.floor((new Date(row.dueDate).getTime() - Date.now()) / 86400000) : 0;
      return days <= 0 ? t('portfolio.rentRoll.status.dueToday') : t('portfolio.rentRoll.status.dueSoon', { count: days });
    }
  }
}

function remindedLabel(iso: string, t: (k: string, o?: any) => string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return t('portfolio.rentRoll.reminder.remindedJustNow');
  if (hours < 48) return t('portfolio.rentRoll.reminder.remindedHours', { count: hours });
  return t('portfolio.rentRoll.reminder.remindedDays', { count: Math.floor(hours / 24) });
}

const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const RentRollCard: React.FC = () => {
  const { t } = useTranslation('common');
  const [refreshKey, setRefreshKey] = useState(0);
  const { status, data, retry } = useListFetch<RentRoll>('/api/properties/rent-roll', { refreshKey });
  const feedback = useWriteFeedback();
  const [confirmRow, setConfirmRow] = useState<RentRollRow | null>(null);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const refetch = () => setRefreshKey((k) => k + 1);

  const doSend = async (row: RentRollRow) => {
    setConfirmRow(null);
    if (!row.invoiceId) return;
    setBusyInvoiceId(row.invoiceId);
    try {
      const res = await fetch('/api/properties/rent-roll/remind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: row.invoiceId }),
      });
      const json = await res.json().catch(() => ({}));
      const outcome: string = json?.status ?? (res.ok ? 'sent' : 'error');
      // Success: no success toast (the row's own "Reminded …" trace is the feedback, §1.3). A
      // window_closed / not_connected refusal is NOT a failure to retry — it resolves to the same
      // honest state the row would show from the start, so we just refetch (this also covers the race
      // case §1.3: a window that closed between render and tap). already_paid (AC1.5 race) likewise
      // refetches so the row flips to Paid; we surface it so the agent knows nothing was sent.
      if (outcome === 'sent') refetch();
      else if (outcome === 'refused') refetch();
      else if (outcome === 'already_paid') { feedback.fail(t('portfolio.rentRoll.reminder.alreadyPaid')); refetch(); }
      else if (outcome === 'no_phone') feedback.fail(t('portfolio.rentRoll.reminder.noPhone'));
      else feedback.fail(t('portfolio.rentRoll.reminder.failed'));
    } catch {
      feedback.fail(t('portfolio.rentRoll.reminder.failed'));
    } finally {
      setBusyInvoiceId(null);
    }
  };

  // I3 §2/AC3.1/AC3.4 — reuses the generic Invoice flow's own route (InvoiceDetailPanel's
  // `markPaid`), one-tap, no confirm (§2 ruling: no hidden external consequence, unlike a WhatsApp
  // send). A 409 means the invoice was independently voided/paid between render and tap — refetch to
  // the real state, never a generic error toast (AC3.4). Any OTHER failure (nothing happened) DOES
  // surface, same discipline as doSend above.
  const doMarkPaid = async (row: RentRollRow) => {
    if (!row.invoiceId) return;
    setBusyInvoiceId(row.invoiceId);
    try {
      const res = await fetch(`/api/invoices/${row.invoiceId}/mark-paid`, { method: 'POST' });
      if (res.ok || res.status === 409) { refetch(); return; }
      feedback.fail(t('portfolio.rentRoll.markPaid.failed'));
    } catch {
      feedback.fail(t('portfolio.rentRoll.markPaid.failed'));
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/properties/generate-invoices', { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      // Success feedback is the rows themselves appearing (the record's own state change is the
      // feedback — the same no-success-toast convention §1.3 follows); only a FAILURE needs a toast.
      refetch();
    } catch {
      feedback.fail(t('portfolio.rentRoll.generate.failed'));
    } finally {
      setGenerating(false);
    }
  };

  const multi = data?.multiProperty ?? false;

  // Action cell for one row. Reminder is offerable only for an unpaid row with an invoice.
  // I3 §2/AC3.2 — Mark paid is ADDITIVE alongside whatever the reminder slot already renders,
  // visible only for a real unpaid current invoice (late/due-soon/open); absent — never
  // disabled — once paid/vacant/unbilled, the same settled-record rule I2 applied to lease actions.
  const renderAction = (row: RentRollRow) => {
    const trace = row.remindedAt ? (
      <div className="text-[11px] text-text-muted mt-0.5" data-testid="portfolio-wa-reminded">{remindedLabel(row.remindedAt, t)}</div>
    ) : null;
    const canMarkPaid = row.invoiceId != null && (row.status === 'late' || row.status === 'due-soon' || row.status === 'open');
    const markPaidChip = canMarkPaid ? (
      <ActionChip
        label={t('invoicesGrid.markPaid')}
        onAct={() => void doMarkPaid(row)}
        disabled={busyInvoiceId === row.invoiceId}
        testId="portfolio-mark-paid"
      />
    ) : null;

    if (row.reachability === 'sendable') {
      return (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <ActionChip
              label={t('portfolio.rentRoll.reminder.send')}
              onAct={() => setConfirmRow(row)}
              disabled={busyInvoiceId === row.invoiceId}
              testId="portfolio-wa-reminder"
            />
            {markPaidChip}
          </div>
          {trace}
        </div>
      );
    }
    if (row.reachability === 'window-closed') {
      // NON-interactive note — no tap target at all (§1.3 point 2). A SystemChip is structurally not a button.
      return (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <SystemChip label={t('portfolio.rentRoll.reminder.closed')} tone="muted" testId="portfolio-wa-closed" />
            {markPaidChip}
          </div>
          {trace}
        </div>
      );
    }
    // not-connected (banner covers it) or no reminder applies (paid/vacant/unbilled) — Mark paid may
    // still apply on its own (e.g. a disconnected account with an open invoice).
    if (markPaidChip) {
      return <div>{markPaidChip}{trace}</div>;
    }
    return trace ?? <span className="text-text-muted">—</span>;
  };

  return (
    <div className="bg-surface border border-border-neutral rounded-xl overflow-hidden" data-testid="portfolio-rent-roll">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-neutral">
        <h3 className="text-sm font-bold text-text-strong m-0">{t('portfolio.rentRoll.title')}</h3>
        {data?.anyUnbilled && (
          <button
            type="button"
            data-testid="portfolio-generate-invoices"
            onClick={doGenerate}
            disabled={generating}
            className="ms-auto min-h-[44px] px-3 rounded-lg border border-primary/40 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50"
          >
            {t('portfolio.rentRoll.generate.button')}
          </button>
        )}
      </div>

      <ListStateShell status={status} testIdBase="portfolio-rent-roll" onRetry={retry}>
        {!data || data.rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-text-muted" data-testid="portfolio-rent-roll-empty">{t('portfolio.rentRoll.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-start border-collapse">
              <thead>
                <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                  {multi && <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.property')}</th>}
                  <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.unit')}</th>
                  <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.tenant')}</th>
                  <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.rent')}</th>
                  <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.status')}</th>
                  <th className="p-cell-y px-cell-x text-start">{t('portfolio.rentRoll.columns.action')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.unitId} className="border-b border-border-neutral last:border-b-0" data-testid="portfolio-rent-row">
                    {multi && <td className="p-cell-y px-cell-x text-text-muted">{row.propertyName}</td>}
                    <td className="p-cell-y px-cell-x font-semibold text-text-strong">{row.unitLabel}</td>
                    <td className="p-cell-y px-cell-x text-text-strong">
                      {row.tenantName ?? <span className="text-text-muted">{t('portfolio.rentRoll.vacant')}</span>}
                    </td>
                    <td className="p-cell-y px-cell-x text-text-strong" dir="ltr">
                      {row.rent != null ? fmtMoney(row.rent) : <span className="text-text-muted">—</span>}
                    </td>
                    <td className="p-cell-y px-cell-x">
                      {row.status === 'unbilled'
                        ? <SystemChip label={statusLabel(row, t)} tone="muted" />
                        : <SystemChip label={statusLabel(row, t)} token={STATUS_TOKEN[row.status]} />}
                    </td>
                    <td className="p-cell-y px-cell-x">{renderAction(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ListStateShell>

      {confirmRow && (
        <ConfirmDialog
          title={t('portfolio.rentRoll.reminder.confirmTitle')}
          message={t('portfolio.rentRoll.reminder.confirm', {
            tenant: confirmRow.tenantName ?? t('portfolio.rentRoll.vacant'),
            amount: confirmRow.rent != null ? fmtMoney(confirmRow.rent) : '',
          })}
          confirmLabel={t('portfolio.rentRoll.reminder.send')}
          onConfirm={() => doSend(confirmRow)}
          onClose={() => setConfirmRow(null)}
        />
      )}
      {feedback.node}
    </div>
  );
};
